import React, { useMemo } from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardMetricItem from '@components/pages/Dashboard/DashboardMetricItem.component'
import type { DashboardUtilizationData } from '@hooks/dashboard/useDashboardUtilizationData'
import type { RangeKey } from '@dataTypes/dashboard.types'
import { mapSchedulingOverviewToAppointmentOverrunMetrics } from '@utils/overviewAppointments.util'
import {
  isDashboardDevMocksEnabled,
  withDashboardMetricShellValues,
} from '@utils/dashboardDevMocks.util'
import {
  hasNonZeroDashboardValues,
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { metricDataToItemPropsWithLayoutClass } from '@utils/dashboardMetrics.util'
import { getOverviewAppointmentOverrunMetrics } from '../overviewMetrics.mockData'

type AppointmentOverrunCardProps = {
  range: RangeKey
  schedulingOverview: DashboardUtilizationData['schedulingOverview']
  schedulingLoading: boolean
}

export function AppointmentOverrunCard({
  range,
  schedulingOverview,
  schedulingLoading,
}: AppointmentOverrunCardProps): React.ReactElement {
  const useMocks = isDashboardDevMocksEnabled()
  const metrics = useMemo(() => {
    const mockMetrics = withDashboardMetricShellValues(
      getOverviewAppointmentOverrunMetrics(range),
      useMocks,
    )
    if (!schedulingOverview) return mockMetrics
    return mapSchedulingOverviewToAppointmentOverrunMetrics(
      schedulingOverview,
      mockMetrics,
    )
  }, [schedulingOverview, range, useMocks])
  const isEmpty = shouldShowDashboardSectionEmpty(
    schedulingLoading,
    schedulingOverview
      ? hasNonZeroDashboardValues(
          schedulingOverview.summary.appointmentOverruns.current,
          schedulingOverview.summary.appointmentOverrunCompleted.current,
          schedulingOverview.summary.appointmentOverrunAvgDelayMinutes.current,
        )
      : hasNonZeroDashboardValues(...metrics.map((metric) => metric.value)),
  )
  const pending = isDashboardSectionPending(
    schedulingLoading,
    schedulingOverview != null,
  )

  return (
    <DashboardSectionCard
      title="Appointment overrun"
      titleHref="/dashboard/appointment-overrun"
      className="dashboard-overview-line-chart-card dashboard-appointment-overrun-card"
      loading={pending}
      skeleton={{ metrics: 3, block: 'none' }}
      isEmpty={isEmpty}
      disableTitleLink={isEmpty}
    >
      <div className="dashboard-overview-acceptance-metrics-row">
        {metrics.map((metric) => (
          <DashboardMetricItem
            key={metric.id ?? metric.leftLabel}
            {...metricDataToItemPropsWithLayoutClass(
              metric,
              'dashboard-overview-acceptance-metric',
            )}
          />
        ))}
      </div>
    </DashboardSectionCard>
  )
}
